import { LoadingOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, Input, Row, Select, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import React, { memo } from 'react';
import { useSupplierQuery } from 'api';
import { Col } from 'components';
import { HasAccess } from 'components/common/accessControl';
import { FilterDrawer } from 'components/common/filterDrawer';
import { CalendarIcon, DeleteIcon, FilterIcon, PlusIcon } from 'components/common/icons';
import { DownloadIcon } from 'components/common/icons/DownloadIcon';
import { PERMISSIONS } from 'constants/permissions';
import { AddModal } from './add';
import { DeleteModal } from './delete';
import styles from './styles.module.scss';
import { usePriceList } from './usePriceList';
import { useAccessControl } from 'components/common/accessControl/useAccessControl';

const { Search } = Input;

const titleNode = (value: string) => (
  <Typography.Text className={'font-size-14'} style={{ color: '#222530' }}>
    {value}
  </Typography.Text>
);
const valueNode = (value: string) => (
  <Typography.Text className={'font-size-14'} color={'#525866'}>
    {value}
  </Typography.Text>
);

export const PriceList = memo(() => {
  const { data = {} } = useSupplierQuery({ page: 1, size: 100 });
  const { content = [] } = data;
  const { hasAccess } = useAccessControl();
  const {
    priceList,
    handleTableChange,
    isLoading,
    pagination,
    onHandleDelete,
    onDeleteModalClose,
    deleteItemId,
    onAddModalStateChange,
    openAddModal,
    filtersForm,
    onFiltersApply,
    resetFilters,
    onFiltersStateChange,
    openFilters,
    setSearch,
  } = usePriceList();

  const columns: ColumnsType<string> = [
    {
      title: titleNode('Supplier'),
      dataIndex: 'supplier',
      key: 'supplier',
      render: (value) => valueNode(value.name),
    },
    {
      title: titleNode('Created Date'),
      dataIndex: 'createDate',
      key: 'createDate',
      sorter: true,
      render: (value) => valueNode(moment.utc(value).format('DD.MM.YYYY')),
    },
    {
      title: titleNode('Expiry Date'),
      dataIndex: 'expireDate',
      key: 'expireDate',
      sorter: true,
      render: (value) => {
        return valueNode(moment.utc(value).format('DD.MM.YYYY'));
      },
    },
    {
      title: titleNode('File Name'),
      key: 'fileName',
      sorter: true,
      width: 200,
      ellipsis: true,
      render: ({ file, fileName }) => {
        return file ? (
          <Button
            type="link"
            icon={<DownloadIcon />}
            download
            target="_blank"
            className={styles.btn_download}
            title={fileName}
            rel="noopener noreferrer"
            href={file}
          >
            <span className={styles.btn_download_text}>{fileName || 'Not Set'}</span>
          </Button>
        ) : (
          ''
        );
      },
    },
  ];

  if (hasAccess(PERMISSIONS.brands.pricelist.delete)) {
    columns.push({
      fixed: 'right',
      width: '36px',
      key: 'actions',
      render: ({ id }: { id: string }) => (
        <Row justify={'center'} className={'bg-white'}>
          <HasAccess role={PERMISSIONS.brands.pricelist.delete}>
            <Button type={'link'} icon={<DeleteIcon />} onClick={() => onHandleDelete(id)} />
          </HasAccess>
        </Row>
      ),
    });
  }

  return (
    <div className={styles.main}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Row align={'middle'} justify={'end'}>
            <HasAccess role={PERMISSIONS.brands.pricelist.write}>
              <Button type={'primary'} onClick={onAddModalStateChange} icon={<PlusIcon />}>
                Add New
              </Button>
            </HasAccess>
          </Row>
        </Col>
        <Col span={24} className={styles.main_container}>
          <Row>
            <Col span={24}>
              <Row justify={'end'} className={styles.actions_container}>
                <Col style={{ width: '300px' }}>
                  <Search enterButton={false} placeholder={'Search'} onChange={(e) => setSearch(e.target.value)} />
                </Col>
                <Button color="default" variant="filled" onClick={onFiltersStateChange} icon={<FilterIcon />} />
              </Row>
            </Col>
            <Col span={24} style={{ paddingTop: '20px' }}>
              <HasAccess role={PERMISSIONS.brands.pricelist.read}>
                <Table
                  bordered
                  size={'small'}
                  columns={columns}
                  dataSource={priceList?.data?.content || []}
                  rowKey="id"
                  loading={{
                    indicator: <LoadingOutlined style={{ fontSize: 48 }} spin />,
                    spinning: isLoading,
                  }}
                  locale={
                    isLoading
                      ? { emptyText: null } // Hide "No Data" text while loading
                      : {} // Use default AntD "No Data" icon when empty
                  }
                  pagination={{
                    position: ['bottomCenter'],
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} items`,
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: priceList?.total || 0,
                    size: 'default',
                  }}
                  onChange={handleTableChange as any}
                />
              </HasAccess>
            </Col>
          </Row>
        </Col>
      </Row>
      <DeleteModal
        openModal={!!deleteItemId}
        onDeleteModalClose={onDeleteModalClose}
        dataToDelete={{ id: deleteItemId }}
      />
      <AddModal openModal={openAddModal} onModalStateChange={onAddModalStateChange} />

      <FilterDrawer
        title="Filter"
        placement="left"
        width={471}
        onClose={onFiltersStateChange}
        open={openFilters}
        onApply={() => filtersForm.submit()}
        onClear={() => {
          filtersForm.resetFields();
          resetFilters();
        }}
        onExpand={onFiltersStateChange}
      >
        <Form layout="vertical" form={filtersForm} onFinish={onFiltersApply}>
          <Row gutter={[0, 16]}>
            <Col span={24}>
              <Form.Item label={'Supplier'} name={'supplierId'}>
                <Select allowClear={true} options={content} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label={'Created Date'} name={'createdDate'}>
                <DatePicker.RangePicker size={'middle'} style={{ width: '100%' }} suffixIcon={<CalendarIcon />} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label={'Expiry Date'} name={'expireDate'}>
                <DatePicker.RangePicker size={'middle'} style={{ width: '100%' }} suffixIcon={<CalendarIcon />} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </FilterDrawer>
    </div>
  );
});
